import { Button, Tooltip, message } from "antd";
import { Mic, MicOff, Video, VideoOff } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSetCandidateMedia } from "@hooks";
import type { ParticipantMedia } from "./useProctoringRoom";

// The candidate's player applies the command and LiveKit reports the new track state; no change by then means it did not react.
const CONFIRM_TIMEOUT_MS = 12000;

type Pending = { device: "camera" | "microphone"; want: boolean; done: string; timer: number };

interface Props {
  examTermId: string;
  examInstanceId: string;
  candidateName: string;
  media?: ParticipantMedia;
  size?: "small" | "middle";
  showLabels?: boolean;
  onSent?: (done: string) => void;
}

/// Remote camera/microphone switches for one named candidate; the new state arrives back as LiveKit mute events.
export const CandidateMediaControls = ({ examTermId, examInstanceId, candidateName, media, size = "small", showLabels = false, onSent }: Props) => {
  const { t } = useTranslation();
  const setMedia = useSetCandidateMedia(examTermId);
  const [pending, setPending] = useState<Pending | null>(null);
  const pendingRef = useRef<Pending | null>(null);
  pendingRef.current = pending;

  const currentOf = (device: Pending["device"]) => (device === "camera" ? !!media?.cameraOn : !!media?.micOn);

  useEffect(() => {
    if (!pending || currentOf(pending.device) !== pending.want) return;
    window.clearTimeout(pending.timer);
    setPending(null);
    message.success(`${candidateName}: ${pending.done}`);
    onSent?.(pending.done);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media?.cameraOn, media?.micOn, pending]);

  useEffect(() => () => window.clearTimeout(pendingRef.current?.timer), []);

  if (!media?.connected) return null;

  const cameraOn = !!media.cameraOn;
  const micOn = !!media.micOn;
  const send = (command: { camera?: boolean; microphone?: boolean }, done: string) => {
    const device: Pending["device"] = command.camera !== undefined ? "camera" : "microphone";
    const want = (command.camera ?? command.microphone) as boolean;
    setMedia.mutate(
      { examInstanceId, ...command },
      {
        onSuccess: () => {
          window.clearTimeout(pendingRef.current?.timer);
          const timer = window.setTimeout(() => {
            setPending(null);
            message.warning(`${candidateName}: ${t("proctoring.commandNoResponse")}`, 8);
          }, CONFIRM_TIMEOUT_MS);
          setPending({ device, want, done, timer });
        },
        onError: () => message.error(`${candidateName}: ${t("proctoring.commandFailed")}`),
      }
    );
  };
  const iconSize = size === "small" ? 14 : 16;
  const cameraLabel = cameraOn ? t("proctoring.turnCameraOff") : t("proctoring.turnCameraOn");
  const micLabel = micOn ? t("proctoring.turnMicOff") : t("proctoring.turnMicOn");
  const cameraText = cameraOn ? t("proctoring.cameraBtnOff") : t("proctoring.cameraBtnOn");
  const micText = micOn ? t("proctoring.micBtnOff") : t("proctoring.micBtnOn");

  return (
    <span style={{ display: "inline-flex", gap: 8 }} onClick={(e) => e.stopPropagation()}>
      <Tooltip title={`${candidateName}: ${cameraLabel}`}>
        <Button
          size={size}
          danger={!cameraOn}
          icon={cameraOn ? <Video size={iconSize} /> : <VideoOff size={iconSize} />}
          loading={(setMedia.isPending && setMedia.variables?.camera !== undefined) || pending?.device === "camera"}
          onClick={() => send({ camera: !cameraOn }, cameraOn ? t("proctoring.cameraTurnedOff") : t("proctoring.cameraTurnedOn"))}
          data-testid="proctor-camera-toggle"
        >
          {showLabels && cameraText}
        </Button>
      </Tooltip>
      <Tooltip title={`${candidateName}: ${micLabel}`}>
        <Button
          size={size}
          danger={!micOn}
          icon={micOn ? <Mic size={iconSize} /> : <MicOff size={iconSize} />}
          loading={(setMedia.isPending && setMedia.variables?.microphone !== undefined) || pending?.device === "microphone"}
          onClick={() => send({ microphone: !micOn }, micOn ? t("proctoring.micTurnedOff") : t("proctoring.micTurnedOn"))}
          data-testid="proctor-mic-toggle"
        >
          {showLabels && micText}
        </Button>
      </Tooltip>
    </span>
  );
};
